<script setup lang="ts">
/**
 * Data sources (ADR 0055) — every system this deployment ingests from.
 *
 * This replaces the "Jobs" tab. Jobs were the wrong noun: an admin does not
 * think "is caj-choir-bc-cron-eu healthy", they think "is Business Central
 * up to date, who can see it, and have we agreed that with its owner". A
 * source is the thing all of those hang off — its scheduled jobs, the
 * corpora and tables it feeds and how they are gated, the assistants that
 * reach it, and its data access agreement.
 *
 * What the Jobs tab was FOR is kept, and kept first: **did the job run at
 * all?** A job that never fires emits nothing, so in a log stream its
 * silence looks like a quiet success. The page still leads with what is
 * overdue, and no job is ever hidden — platform housekeeping and anything
 * nobody has described each get their own section.
 *
 * A source's detail answers, in order: what it is, its agreement (and a
 * way to get it signed), who can see its data (and the data owner's
 * confirmation of that), whether its jobs run, and — folded, for whoever
 * configures it — the tags, tables and files behind it. Setting up
 * connections is a once-a-quarter job, so it sits below everything else.
 */
import { vWrite } from "../admin-read-only";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import {
  AdminError,
  type AgreementResponse,
  type AgreementState,
  type DataSource,
  type DataSourcesResponse,
  type ClearanceClass,
  type GovernanceAssistant,
  type Team,
  type JobRow,
  getDataSourceAgreement,
  getDataSources,
  getGovernance,
  getTeams,
} from "../api/admin";
import { useToast } from "../composables/useToast";
import { accessModelText, accessModelTone } from "../utils/governance";
import { adminParam } from "../utils/vocabulary";
import AgreementCard from "./AgreementCard.vue";
import AgreementViewer from "./AgreementViewer.vue";
import SourceAccess from "./SourceAccess.vue";
import SourceHealth from "./SourceHealth.vue";
import JobsTable from "./JobsTable.vue";
import SidePanel from "./SidePanel.vue";
import ConnectionsPanel from "./ConnectionsPanel.vue";
import FoldSection from "./FoldSection.vue";
import HelpHint from "./HelpHint.vue";
import ManagedSources from "./ManagedSources.vue";
import SkeletonRows from "./SkeletonRows.vue";
import StatusChip from "./StatusChip.vue";
import { fmtAge, fmtDateTime } from "../utils/datetime";

const toast = useToast();
const props = defineProps<{ devUser: string }>();
const managed = ref<InstanceType<typeof ManagedSources> | null>(null);

/** What a folded list of jobs says while closed. Late outranks failing: a
 *  failing job tells you why in the Logs tab; a late one emits nothing. */
function jobsSummary(jobs: { overdue: boolean; last_status: string }[]): {
  text: string;
  tone: "neutral" | "warn" | "bad";
} {
  const late = jobs.filter((j) => j.overdue).length;
  const failed = jobs.filter(
    (j) => !j.overdue && j.last_status.toLowerCase() === "failed",
  ).length;
  const n = `${jobs.length} ${jobs.length === 1 ? "job" : "jobs"}`;
  if (late)
    return { text: `${n} · ${late} not running on schedule`, tone: "bad" };
  if (failed)
    return { text: `${n} · ${failed} failed last time`, tone: "warn" };
  return { text: `${n} · all on time`, tone: "neutral" };
}

const data = ref<DataSourcesResponse | null>(null);
const loading = ref(false);
const error = ref("");
const expanded = ref<Set<string>>(new Set());

const agreement = ref<AgreementResponse | null>(null);
const agreementOpen = ref(false);
const agreementLoading = ref("");

function fail(e: unknown, title: string): string {
  const msg = e instanceof AdminError ? e.message : String(e);
  toast.error(title, msg);
  return msg;
}

async function reload() {
  loading.value = true;
  error.value = "";
  try {
    data.value = await getDataSources(props.devUser);
  } catch (e) {
    error.value = fail(e, "Could not load data sources");
  } finally {
    loading.value = false;
  }
  void openFromHash();
}

/** Governance rows, for who-can-see and the data owner's confirmation.
 *  Non-fatal: without them the access panel shows less, nothing breaks. */
const gov = ref<Map<string, GovernanceAssistant>>(new Map());
async function loadGov(): Promise<void> {
  try {
    const g = await getGovernance(props.devUser, 1);
    gov.value = new Map(g.assistants.map((a) => [a.slug, a]));
  } catch {
    gov.value = new Map();
  }
}

/** Teams and data classes: who is cleared for a restricted part. */
const teams = ref<Team[]>([]);
const classes = ref<ClearanceClass[]>([]);
async function loadTeams(): Promise<void> {
  try {
    const t = await getTeams(props.devUser);
    teams.value = t.teams;
    classes.value = t.clearance_classes ?? [];
  } catch {
    teams.value = [];
    classes.value = [];
  }
}

function onGovUpdated(a: GovernanceAssistant): void {
  gov.value = new Map(gov.value).set(a.slug, a);
}

function onSourceUpdated(updated: DataSource): void {
  const d = data.value;
  if (!d) return;
  d.sources = d.sources.map((s) => (s.key === updated.key ? updated : s));
}

/** `#/admin?tab=data-sources&source=risma` opens that source — by key, or
 *  by name or by one of its scope tags, which is what other screens know it
 *  as. */
async function openFromHash(): Promise<void> {
  const want = adminParam("source").toLowerCase();
  const hit = data.value?.sources.find(
    (s) =>
      s.key === want ||
      s.label.toLowerCase() === want ||
      s.scope_tags.includes(want),
  );
  if (!hit) return;
  const key = hit.key;
  expanded.value = new Set([...expanded.value, key]);
  await nextTick();
  document
    .getElementById(`source-${key}`)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

onMounted(() => {
  void reload();
  void loadGov();
  void loadTeams();
  window.addEventListener("hashchange", openFromHash);
});
onUnmounted(() => window.removeEventListener("hashchange", openFromHash));

function names(): Record<string, string> {
  return data.value?.assistant_names ?? {};
}

function agreementText(s: DataSource): string {
  const m = s.agreement_meta;
  if (s.agreement_state === "draft" && m)
    return `Unsigned · ${m.signatures.dated} of ${m.signatures.needed}`;
  return AGREEMENT[s.agreement_state].text;
}

/** Every job once, however many sources it serves. */
const allJobs = computed<JobRow[]>(() => {
  const d = data.value;
  if (!d) return [];
  const seen = new Map<string, JobRow>();
  for (const j of [
    ...d.sources.flatMap((s) => s.jobs),
    ...d.platform_jobs,
    ...d.other_jobs,
  ])
    seen.set(j.name, j);
  return Array.from(seen.values());
});

const overdue = computed(() => allJobs.value.filter((j) => j.overdue));

/**
 * Unattended failures only — Schedule and Event triggers. A Manual job's
 * last outcome is the operator's own business: they were there when it ran,
 * and surfacing it as a standing alert means the banner stays red until
 * someone re-triggers the job purely to clear it.
 */
const failing = computed(() =>
  allJobs.value.filter(
    (j) =>
      !j.overdue &&
      j.trigger_type !== "Manual" &&
      (j.failure_open ?? j.last_status.toLowerCase() === "failed"),
  ),
);

function toggle(key: string) {
  if (expanded.value.has(key)) expanded.value.delete(key);
  else expanded.value.add(key);
  expanded.value = new Set(expanded.value);
}

async function openAgreement(s: DataSource) {
  if (agreementLoading.value) return;
  agreementLoading.value = s.key;
  try {
    agreement.value = await getDataSourceAgreement(s.key, props.devUser);
    agreementOpen.value = true;
  } catch (e) {
    fail(e, "Could not load the agreement");
  } finally {
    agreementLoading.value = "";
  }
}

/** AdminPage routes on the hash; no router to inject. */
function goTo(tab: string) {
  window.location.hash = `#/admin?tab=${tab}`;
}

const KIND_LABELS: Record<DataSource["kind"], string> = {
  documents: "Documents",
  facts: "Tables",
  "documents+facts": "Documents + tables",
  "user-content": "User content",
};

const HEALTH: Record<DataSource["health"], { dot: string; text: string }> = {
  failing: { dot: "dot--warn", text: "Last run failed" },
  overdue: { dot: "dot--bad", text: "Not run when expected" },
  ok: { dot: "dot--ok", text: "Running on schedule" },
  none: { dot: "", text: "No scheduled job" },
};

const AGREEMENT: Record<
  AgreementState,
  { tone: "ok" | "warn" | "bad" | "muted"; text: string }
> = {
  signed: { tone: "ok", text: "Signed" },
  draft: { tone: "warn", text: "Unsigned" },
  blocked: { tone: "bad", text: "Can't be signed yet" },
  missing: { tone: "bad", text: "Agreement not found" },
  exempt: { tone: "muted", text: "None needed" },
};

/** How a corpus or table decides what one person sees (ADR 0053). */
const ENFORCEMENT: Record<string, string> = {
  mirrored: "per person, as in the source",
  assigned: "per person — mapping kept here",
  own: "the person's own data",
  group: "one group sees all of it",
  open: "public",
};

/** The newest start across the source's jobs, as an ISO string. */
function lastRunAt(s: DataSource): string {
  const starts = s.jobs
    .map((j) => j.last_start)
    .filter(Boolean)
    .sort();
  return starts[starts.length - 1] ?? "";
}
</script>

<template>
  <div class="admin-sources">
    <header class="admin-sources__header">
      <h2>
        Data sources
        <HelpHint wide align="start" label="About data sources">
          Every system this assistant platform reads from: whether its refresh
          is actually running, what it feeds, who can reach it, and the
          agreement with its owner. The
          <button class="link" @click="goTo('logs')">Logs tab</button> can tell
          you why a run failed, but not that a run never happened — a job that
          doesn't fire emits nothing.
        </HelpHint>
      </h2>
      <div class="admin-sources__right">
        <button v-write class="btn btn--primary" @click="managed?.openAdd()">
          + Add source
        </button>
      </div>
    </header>

    <ManagedSources ref="managed" :dev-user="devUser" />

    <p v-if="error" class="error">{{ error }}</p>

    <template v-if="data && !error">
      <p v-if="!data.jobs_available" class="warn">
        Job status is not available: {{ data.jobs_reason }}
      </p>
      <p v-else-if="overdue.length" class="alert">
        <strong>{{ overdue.length }}</strong>
        {{ overdue.length === 1 ? "job has" : "jobs have" }} not run when
        expected.
      </p>
      <p v-else-if="failing.length" class="warn">
        Everything is firing on schedule, but
        <strong>{{ failing.length }}</strong>
        unattended {{ failing.length === 1 ? "job" : "jobs" }} last finished
        <em>failed</em>.
      </p>
      <p v-else class="muted small">All scheduled jobs are firing on time.</p>

      <table class="admin-table sources">
        <thead>
          <tr>
            <th></th>
            <th>Source</th>
            <th>Last refreshed</th>
            <th>Used by</th>
            <th title="How the source decides who sees what (ADR 0067)">
              Access
            </th>
            <th>Agreement</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="s in data.sources" :key="s.key">
            <tr
              :id="`source-${s.key}`"
              :class="[
                'source-row',
                s.health === 'overdue' && 'row--overdue',
                expanded.has(s.key) && 'row--open',
              ]"
              :aria-expanded="expanded.has(s.key)"
              @click="toggle(s.key)"
            >
              <td>
                <span
                  :class="['dot', HEALTH[s.health].dot]"
                  :aria-label="HEALTH[s.health].text"
                  role="img"
                />
              </td>
              <td>
                <div class="source__label">{{ s.label }}</div>
                <div class="muted small">{{ s.system }}</div>
              </td>
              <td class="small">
                <span :title="fmtDateTime(lastRunAt(s))">{{
                  fmtAge(lastRunAt(s))
                }}</span>
                <div
                  v-if="s.health !== 'ok' && s.health !== 'none'"
                  class="small gap-text"
                >
                  {{ HEALTH[s.health].text }}
                </div>
              </td>
              <td class="small">
                <span v-if="s.kind === 'user-content'"
                  >each person, their own</span
                >
                <span v-else-if="s.assistants.length">
                  {{ s.assistants.length }}
                  {{ s.assistants.length === 1 ? "assistant" : "assistants" }}
                </span>
                <span v-else class="muted">—</span>
              </td>
              <td>
                <StatusChip
                  :tone="accessModelTone(s)"
                  :title="s.access_how"
                  dot
                  >{{ accessModelText(s) }}</StatusChip
                >
              </td>
              <td>
                <StatusChip :tone="AGREEMENT[s.agreement_state].tone">
                  {{ agreementText(s) }}
                </StatusChip>
              </td>
              <td>
                <button class="link" @click.stop="toggle(s.key)">
                  {{ expanded.has(s.key) ? "hide" : "details" }}
                </button>
              </td>
            </tr>

            <tr v-if="expanded.has(s.key)" class="source-detail">
              <td colspan="7">
                <p class="source__summary">{{ s.summary }}</p>
                <dl class="facts">
                  <dt>Brings in</dt>
                  <dd>{{ KIND_LABELS[s.kind] }}</dd>
                  <dt>Where</dt>
                  <dd>{{ s.where || "—" }}</dd>
                  <dt>Signs in as</dt>
                  <dd>{{ s.credential || "—" }}</dd>
                  <dt>Access</dt>
                  <dd>
                    <strong>{{ accessModelText(s) }}</strong
                    ><template v-if="s.access_how"> — {{ s.access_how }}</template>
                  </dd>
                  <dt>Data owner</dt>
                  <dd>{{ s.owner || "—" }}</dd>
                  <dt>Refresh</dt>
                  <dd>{{ s.refresh || "—" }}</dd>
                </dl>

                <SourceHealth :source="s" />

                <h4>Agreement</h4>
                <AgreementCard
                  :source="s"
                  :dev-user="devUser"
                  @updated="onSourceUpdated"
                  @read="openAgreement(s)"
                />

                <template v-if="s.assistants.length">
                  <h4>Who can see this data</h4>
                  <SourceAccess
                    :source="s"
                    :names="names()"
                    :gov="gov"
                    :teams="teams"
                    :classes="classes"
                    :dev-user="devUser"
                    @gov-updated="onGovUpdated"
                  />
                </template>
                <p v-else-if="s.kind !== 'user-content'" class="muted small">
                  No assistant reads this source yet.
                </p>

                <h4>Scheduled jobs</h4>
                <JobsTable
                  :jobs="s.jobs"
                  :empty-text="
                    s.job_tokens.length
                      ? `No job matching ${s.job_tokens.join(', ')} is reporting here.`
                      : 'Loaded by hand — no scheduled job.'
                  "
                />

                <FoldSection
                  class="tech"
                  title="Technical details"
                  :level="4"
                  summary="indexes, tables and configuration"
                >
                  <table
                    v-if="s.corpora.length || s.tables.length"
                    class="inner inner--wide"
                  >
                    <tr v-for="c in s.corpora" :key="`c:${c.tag}`">
                      <td class="small">Documents tagged</td>
                      <td>
                        <code class="small">{{ c.tag }}</code>
                        <span v-if="c.label" class="muted small">
                          {{ c.label }}</span
                        >
                      </td>
                      <td class="small">
                        <span v-if="c.status === 'reserved'" class="muted"
                          >reserved — nothing ingested yet</span
                        >
                        <span v-else>{{
                          ENFORCEMENT[c.enforcement] ?? "not declared"
                        }}</span>
                      </td>
                    </tr>
                    <tr v-for="t in s.tables" :key="`t:${t.table}`">
                      <td class="small">Table</td>
                      <td>
                        <code class="small">{{ t.table }}</code>
                      </td>
                      <td class="small">
                        {{
                          t.row_security
                            ? ENFORCEMENT.assigned
                            : ENFORCEMENT.group
                        }}
                      </td>
                    </tr>
                  </table>
                  <p
                    v-else-if="s.fact_tables.length || s.scope_tags.length"
                    class="muted small"
                  >
                    Nothing registered yet — its tables appear once the first
                    rebuild has run.
                  </p>
                  <p class="muted small">
                    <template v-if="s.plugins.length"
                      >Connector: <code>{{ s.plugins.join(", ") }}</code
                      >.
                    </template>
                    <template v-if="s.manifests.length"
                      >Configured in <code>{{ s.manifests.join(", ") }}</code
                      >.
                    </template>
                    <template v-if="s.agreement"
                      >Agreement text:
                      <code>config/compliance/{{ s.agreement }}.md</code
                      >.</template
                    >
                  </p>
                </FoldSection>
              </td>
            </tr>
          </template>
          <tr v-if="data.sources.length === 0">
            <td colspan="7" class="muted">
              No data sources are described yet — add them to
              <code>config/data-sources.yaml</code>.
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Housekeeping, and jobs nobody has described: reference, not the
           reason anyone opens this tab. One line each — which opens itself,
           and turns amber or red, when a job inside is late or failing. -->
      <FoldSection
        v-if="data.platform_jobs.length"
        class="block"
        title="Platform jobs"
        :level="3"
        :summary="jobsSummary(data.platform_jobs).text"
        :tone="jobsSummary(data.platform_jobs).tone"
        :open="jobsSummary(data.platform_jobs).tone !== 'neutral'"
      >
        <p class="muted small">
          Housekeeping that keeps the platform itself in order — these bring no
          data in.
        </p>
        <JobsTable :jobs="data.platform_jobs" />
      </FoldSection>
      <FoldSection
        v-if="data.other_jobs.length"
        class="block"
        title="Jobs nobody has described"
        :level="3"
        :summary="jobsSummary(data.other_jobs).text"
        :tone="jobsSummary(data.other_jobs).tone"
        :open="jobsSummary(data.other_jobs).tone !== 'neutral'"
      >
        <p class="muted small">
          Running here, but not listed under any source in
          <code>config/data-sources.yaml</code>. If one of these is an ingest,
          it has no agreement and no owner on this page.
        </p>
        <JobsTable :jobs="data.other_jobs" />
      </FoldSection>
    </template>
    <SkeletonRows v-else-if="loading" :rows="8" />

    <button class="btn" @click="reload">Refresh</button>

    <!-- Setup: which Microsoft 365 connections and site grants exist. Done
         when a source is added, rarely after — so below the daily view. -->
    <ConnectionsPanel class="block" :dev-user="devUser" />

    <SidePanel
      v-model="agreementOpen"
      :title="agreement?.meta.id || 'Data access agreement'"
      :subtitle="agreement?.label ?? ''"
    >
      <AgreementViewer v-if="agreement" :agreement="agreement" />
    </SidePanel>
  </div>
</template>

<style scoped>
.admin-sources {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: flex-start;
}
.admin-sources__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.admin-sources__right {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.sources,
.block {
  width: 100%;
}
.source-row {
  cursor: pointer;
}
.row--overdue {
  background: var(--color-error-bg);
}
.row--open td {
  border-bottom-color: transparent;
}
.source__label {
  font-weight: 600;
}
.source-detail {
  background: var(--color-surface-alt);
}
.source-detail > td {
  padding: 0.75rem 1rem 1rem 2.25rem;
}
.source-detail h4 {
  margin: 1rem 0 0.35rem;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.tech {
  margin-top: 1rem;
}
.source__summary {
  margin: 0 0 0.5rem;
}
.facts {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.2rem 1rem;
  margin: 0;
  font-size: 0.85rem;
}
.facts dt {
  color: var(--color-muted);
}
.facts dd {
  margin: 0;
}
.inner {
  border-collapse: collapse;
}
.inner--wide {
  width: 100%;
}
.inner td {
  padding: 0.2rem 0.8rem 0.2rem 0;
  vertical-align: top;
}
/* Shape + label carry the meaning too — colour alone is not a signal. */
.dot {
  display: inline-block;
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--color-muted);
}
.dot--ok {
  background: var(--color-success);
}
.dot--warn {
  background: var(--color-warn);
}
.dot--bad {
  background: var(--color-error);
}
.alert {
  margin: 0;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-error);
  background: var(--color-error-bg);
  color: var(--color-error);
}
.warn {
  margin: 0;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-warn);
  background: var(--color-warn-bg);
  color: var(--color-warn-ink);
}
.gap-text {
  color: var(--color-error);
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.82rem;
}
.error {
  color: var(--color-error);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: inherit;
  padding: 0;
}
</style>
